import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { INVOICE_SORT_DIRECTION, ListInvoiceStore } from './invoice-list.store';
import {
    BaseSiteLayoutModule,
    DayjsModule,
    DisplayCurrencyModule,
    FormatDateRangeModule,
    HighlightModule,
    ProcessedStatusDisplayModule,
    SupplierDisplayNamePipe,
} from '@lib/util';
import { InvoiceStatusDisplayComponent } from '@lib/invoice-public';
import { TranslateModule } from '@ngx-translate/core';
import {
    IimAutocompleteModule,
    IimPagination,
    IimPaginationModule,
    IimTableModule,
    TableSortDirection,
    TableSortState,
} from '@iim-angular/components';
import { CopyToClipboardModule, SortDirection, View } from '@lib/core';
import { ActivatedRoute, Params, Router, RouterLink } from '@angular/router';
import { ListInvoice } from './types/ListInvoice';
import { InvoiceListPreviewComponent } from './preview/invoice-list-preview.component';
import { PaginationTranslation } from '@lib/i18n';
import { UserPaginationService } from '@lib/user';
import { ListInvoiceFilter } from './types/ListInvoiceFilter';
import { ListInvoicePagination } from './types/ListInvoicePagination';
import { ListInvoiceStatus } from './types/ListInvoiceStatus';
import { ListInvoiceSorting } from './types/ListInvoiceSorting';
import { debounceTime, Subject } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { BC, BreadcrumbService } from '@lib/shared/breadcrumbs';
import { MatTooltip } from '@angular/material/tooltip';

@Component({
    selector: 'invoice-list',
    imports: [
        SupplierDisplayNamePipe,
        CommonModule,
        BaseSiteLayoutModule,
        TranslateModule,
        CopyToClipboardModule,
        DayjsModule,
        HighlightModule,
        IimAutocompleteModule,
        IimTableModule,
        IimPaginationModule,
        RouterLink,
        ProcessedStatusDisplayModule,
        InvoiceStatusDisplayComponent,
        InvoiceListPreviewComponent,
        FormsModule,
        FormatDateRangeModule,
        MatTooltip,
        DisplayCurrencyModule,
    ],
    templateUrl: './invoice-list.component.html',
    styleUrl: './invoice-list.component.scss',
    providers: [ListInvoiceStore],
})
export class InvoiceListComponent implements OnInit {
    public store = inject(ListInvoiceStore);

    public paginationTranslations: PaginationTranslation;
    public filterChanged$: Subject<void> = new Subject<void>();

    private readonly paginationService = inject(UserPaginationService);
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly destroyRef = inject(DestroyRef);
    private readonly breadcrumbService = inject(BreadcrumbService);

    protected readonly TableSortDirection = TableSortDirection;
    protected readonly ListInvoiceStatus = ListInvoiceStatus;

    constructor() {
        this.paginationTranslations = this.paginationService.getPaginationTranslations();
    }

    async ngOnInit(): Promise<void> {
        this.breadcrumbService.set(BC.MY_ADMIN.LIST, BC.MY_ADMIN.INVOICE.LIST);
        await this.store.init(this.route.snapshot.queryParams);
        this.applyFiltersToRoute(this.store.filter(), this.store.pagination(), this.store.sortDirection());
        this.store.loadData();
        this.listenToRouteChanges();
        this.listenToFilterChanges();
    }

    public expandChange(invoice: ListInvoice, isExpanded: boolean): void {
        if (isExpanded && !invoice.fullyLoaded) {
            this.store.lazyLoadPreview(invoice);
        }
    }

    paginationChanged(event: IimPagination) {
        const filter = this.store.filter();
        const sortDirection = this.store.sortDirection();

        const pagination = new ListInvoicePagination(event);
        const currentPagination = this.store.pagination();

        if (currentPagination.elementsPerPage !== pagination.elementsPerPage) {
            this.store.setPagination(currentPagination.withPage(1));
        }

        this.applyFiltersToRoute(filter, pagination, sortDirection);
    }

    public onSortChange(sortState: TableSortState): void {
        const direction = sortState?.direction === TableSortDirection.ASC ? SortDirection.ASC : SortDirection.DESC;
        const pagination = this.store.pagination().withPage(1);
        this.store.setPagination(pagination);
        this.store.setSorting(direction);

        // Map sortingHeaderName to ListInvoiceSorting enum
        let sortBy: ListInvoiceSorting = ListInvoiceSorting.CREATE_DATE; // default
        if (sortState?.column.sortingHeaderName === 'documentDate') {
            sortBy = ListInvoiceSorting.DOCUMENT_DATE;
        }
        this.store.setSortBy(sortBy);

        this.applyFiltersToRoute(this.store.filter(), pagination, direction);
    }

    private listenToRouteChanges(): void {
        this.route.queryParams.subscribe((params: Params) => {
            const updatedFilter = ListInvoiceFilter.fromRouteParams(params);
            this.store.setFilter(updatedFilter);
            const updatedPagination = ListInvoicePagination.fromRouteParams(params);
            this.store.setPagination(updatedPagination);
            const sortDirection =
                params[INVOICE_SORT_DIRECTION] === SortDirection.ASC ? SortDirection.ASC : SortDirection.DESC;
            this.store.setSorting(sortDirection);
            this.store.loadData();
        });
    }

    private listenToFilterChanges(): void {
        this.filterChanged$.pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            const filter = this.store.filter();
            const pagination = this.store.pagination().withPage(1);
            this.store.setPagination(pagination);
            const sortDirection = this.store.sortDirection();
            this.applyFiltersToRoute(filter, pagination, sortDirection);
        });
    }

    private applyFiltersToRoute(
        filter: ListInvoiceFilter,
        pagination: ListInvoicePagination,
        sortDirection: SortDirection,
    ): void {
        this.router.navigate([View.CLIENT, 'my-admin', 'invoice', 'list'], {
            queryParams: {
                ...filter.toRouteParams(),
                ...pagination.toRouteParams(),
                [INVOICE_SORT_DIRECTION]: sortDirection,
            },
        });
    }
}
